/* eslint-disable no-nested-ternary */
import * as React from 'react';
import styled from 'styled-components';

import tableColumnsFormatter from '../../utils/tableColumnsFormatter';
import DashboardLayout from '../../layouts/DashboardLayout';
import {
  getInspectionTypes,
  createInspectionType,
  updateInspectionType,
  deleteInspectionType,
  InspectionTableType,
} from '../../api/inspectionTypes';
import { getJurisdictions } from '../../api/jurisdiction';
import { GET_ALL_QUERY_PARAMS } from '../../constants';
import Table from '../../organisms/Table';
import ActionsPanel from '../../organisms/ActionsPanel';
import {
  ModalDefaultDraggable,
  ModalDeleteConfirmation,
} from '../../molecules';
import ModalInfo from '../../molecules/ModalInfo';
import useTablePage from '../../hooks/useTablePage';
import { convertTableBoolean } from '../../utils/convertTableValues';
import InspectionTypeForm from '../../forms/InspectionTypeForm';
import useUserRole from '../../hooks/useUserRole';
import { Loader } from '../../atoms';

const InspectionTypesPage = () => {
  const {
    search,
    setSearch,
    modalError,
    modalSuccess,
    isModalOpen,
    selectedItem,
    setSelectedItem,
    modalTitle,
    values,
    errors,
    modalErrorTitle,
    tableManuallyUpdate,
    createOrUpdate,
    deleteById,
    handleAdd,
    handleEdit,
    handleDelete,
    handleCloseModal,
    handleCloseModalInfo,
  } = useTablePage<InspectionTableType>({
    createEndpoint: createInspectionType,
    updateEndpoint: updateInspectionType,
    deleteEndpoint: deleteInspectionType,
    modalCreateTitle: 'Add Inspection Type',
    modalUpdateTitle: 'Update Inspection Type',
  });
  const { isSuperAdmin, isFranchiseAdmin } = useUserRole();

  const [
    jurisdictionsData,
    setJurisdictionsData,
  ] = React.useState([]);

  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    setIsLoading(true);

    Promise.all([
      getJurisdictions({
        ...GET_ALL_QUERY_PARAMS,
        ordering: 'name',
      }),
    ])
      .then(([jurisdictionsResponse]) => {
        setJurisdictionsData(
          jurisdictionsResponse.data.results,
        );
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  const columnsOrder = [
    'name',
    'jurisdictionName',
    'tradeName',
    'isActive',
  ];

  const columns = column => {
    if (
      [
        'id',
        'isDeleted',
        'jurisdictionId',
        'tradeIds',
        'linkedInspectionReport',
        isFranchiseAdmin ? 'isDefault' : null,
      ].includes(column)
    ) {
      return null;
    }

    return tableColumnsFormatter(column);
  };

  const handleCloseForm = () => {
    if (!modalError && !modalSuccess) {
      handleCloseModal();
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageWrapper>
        <ActionsPanel
          onSearch={setSearch}
          onAdd={
            isSuperAdmin
              ? handleAdd
              : isFranchiseAdmin && jurisdictionsData.length
              ? handleAdd
              : null
          }
        />
      <Container>
        <Table
          customSearch
          searchQuery={search}
          columns={columns}
          columnsOrder={columnsOrder}
          rows={getInspectionTypes}
          onEdit={
            isSuperAdmin
              ? handleEdit
              : isFranchiseAdmin && jurisdictionsData.length
              ? handleEdit
              : null
          }
          onDelete={isSuperAdmin && handleDelete}
          formatValue={convertTableBoolean}
          manuallyUpdate={tableManuallyUpdate}
        />
      </Container>

      {isModalOpen && (
        <ModalDefaultDraggable
          onClose={handleCloseForm}
          title={modalTitle}
        >
          <InspectionTypeForm
            initialValues={values}
            modalError={modalErrorTitle.length > 0}
            initialErrors={errors}
            onSubmit={createOrUpdate}
          />
        </ModalDefaultDraggable>
      )}

      {selectedItem && (
        <ModalDeleteConfirmation
          onCancel={() => setSelectedItem(null)}
          onConfirm={() => deleteById(selectedItem.id)}
        >
          <strong>{selectedItem.name}</strong>
        </ModalDeleteConfirmation>
      )}

      {(modalSuccess || modalError) && (
        <ModalInfo
          timeToClose={5000}
          isError={modalError}
          onClose={handleCloseModalInfo}
          errorMsg={modalErrorTitle}
        />
      )}
      </PageWrapper>
    </DashboardLayout>
  );
};

const PageWrapper = styled.div`
  width: 100%;
  padding: 20px 16px 0;
  box-sizing: border-box;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';

  @media (max-width: 840px) {
    padding: 12px 12px 0;
  }
`;

const Container = styled.div`
  margin: 20px 0 0 0;
`;

const LoaderContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export default InspectionTypesPage;
